import React, { useEffect, useState } from 'react';
import Inquiry from './Inquiry';
import Result from './Result';
import './CardSelectorTool.scss';
import '../../App.scss';
import { APP_NAME, Card, ServicePlanId } from '../../common';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { Age } from '../../common';
import { Message } from '@messageformat/react';
import { ServicePlansInfoApi } from '../../api/ServicePlansInfoApi';
import { ServicePlan } from '../../domain/ServicePlan';
import { Loading } from '@op/opds.opux';
import { cleanSessionStorage } from '../../util/commonUtil';

export enum CardSelectorToolState {
  INQUIRY,
  RESULT,
}

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.stopImmediatePropagation();
  event.returnValue = '';
  return event;
};

// Cannot use useSessionStorage hook here, so interacting directly with session storage
export const requiredKeysInSessionStorage = (): boolean => {
  if (
    sessionStorage.getItem(`${APP_NAME}-cardPoints`) &&
    sessionStorage.getItem(`${APP_NAME}-cardSelectorToolState`) &&
    sessionStorage.getItem(`${APP_NAME}-age`) &&
    sessionStorage.getItem(`${APP_NAME}-recommendations`)
  ) {
    return true;
  } else {
    return false;
  }
};

export enum AnalyticsPhaseCategories {
  START = 'start',
  STEP = 'step',
  COMPLETED = 'completed',
}

// prettier-ignore
export const analyticsPhases = new Map<number | string, string>([
  [1, "1. Minka ikainen olet?"],
  [2, "2. Tuoteturva"],
  [3, "3. Joustoa yllattaviin menoihin"],
  [4, "4. Teetko ostoksia verkossa?"],
  [5, "5. Identiteettiturva"],
  [6, "6. Lipputurva"],
  [7, "7. Matkustaminen"],
  [8, "8. Lentokenttalounget"],
  ["result", "9. Korttisuositukset"]
]);

// Tests use mock version of api which is passed via props
interface CardSelectorToolProps {
  api?: ServicePlansInfoApi;
}

const CardSelectorTool = (props: CardSelectorToolProps) => {
  const sessionStorage = useSessionStorage();

  const [cardSelectorToolState, setCardSelectorToolState] = useState<CardSelectorToolState>(() => {
    const stateInSessionJson = sessionStorage.getItem('cardSelectorToolState');
    if (requiredKeysInSessionStorage() && stateInSessionJson) {
      try {
        const stateInSession: CardSelectorToolState = JSON.parse(stateInSessionJson);
        return stateInSession;
      } catch (error) {
        console.error('Error parsing data from session storage. Using default card selector tool state.'); // eslint-disable-line no-console
      }
    }
    return CardSelectorToolState.INQUIRY;
  });

  const [age, setAge] = useState<Age>(() => {
    const ageInSessionJson = sessionStorage.getItem('age');
    if (requiredKeysInSessionStorage() && ageInSessionJson) {
      try {
        const ageInSession: Age = JSON.parse(ageInSessionJson);
        return ageInSession;
      } catch (error) {
        console.error('Error parsing data from session storage. Could not get age.'); // eslint-disable-line no-console
        cleanSessionStorage();
        setCardSelectorToolState(CardSelectorToolState.INQUIRY);
      }
    }
    return Age.AGE_OVER_30;
  });
  const [hasInquiryProgressed, setHasInquiryProgressed] = useState<boolean>(false);
  const [servicePlans, setServicePlans] = useState<Map<ServicePlanId, ServicePlan>>();

  const [cardPoints, setCardPoints] = useState<Map<Card, number>>(() => {
    const cardPointsInSessionJson = sessionStorage.getItem('cardPoints');
    if (requiredKeysInSessionStorage() && cardPointsInSessionJson) {
      try {
        const cardPointsInSession: Map<Card, number> = new Map(JSON.parse(cardPointsInSessionJson));
        return cardPointsInSession;
      } catch (error) {
        console.error('Error parsing data from session storage. Starting cardPoints calculation from the beginning.'); // eslint-disable-line no-console
        setCardSelectorToolState(CardSelectorToolState.INQUIRY);
      }
    }
    return new Map<Card, number>([
      [Card.CLASSIC, 0],
      [Card.DEBIT, 0],
      [Card.DUO, 0],
      [Card.GOLD, 0],
      [Card.PLATINUM, 0],
    ]);
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cardSelectorToolState === CardSelectorToolState.RESULT || !hasInquiryProgressed) {
      window.removeEventListener('beforeunload', onBeforeUnload);
    } else {
      window.addEventListener('beforeunload', onBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [cardSelectorToolState, hasInquiryProgressed]);

  useEffect(() => {
    const initServicePlans = () => {
      setLoading(true);
      const api = props.api || new ServicePlansInfoApi();
      const servicePlansMap = new Map<ServicePlanId, ServicePlan>();
      api
        .getServicePlans()
        .then((plans) => {
          for (const plan of plans['servicePlans']) {
            const planObj = new ServicePlan().deserialize(plan);
            servicePlansMap.set(planObj.id, planObj);
          }
          if (!servicePlansMap.size) {
            throw new Error('Got response but there were no service plans.');
          }
          setServicePlans(servicePlansMap);
        })
        .catch((e) => setError(e.message));
    };

    initServicePlans();
    setLoading(false);
  }, [props.api]);

  useEffect(() => {
    if (servicePlans && servicePlans.size) setLoading(false);
  }, [servicePlans]);

  useEffect(() => {
    if (error) throw new Error(error);
  }, [error]);

  return (
    <div className="service-plan-selector" data-testid="service-plan-selector">
      <h1 className="pac-screen-reader-only">
        <Message id="paketinvalintatyokalu:paaotsikko" />
      </h1>
      {cardSelectorToolState === CardSelectorToolState.INQUIRY && !loading && (
        <Inquiry
          stateHandler={setCardSelectorToolState}
          cardPoints={cardPoints}
          setCardPoints={setCardPoints}
          setAge={setAge}
          setHasInquiryProgressed={setHasInquiryProgressed}
        />
      )}
      {cardSelectorToolState === CardSelectorToolState.RESULT && !loading && servicePlans && servicePlans.size > 0 && (
        <Result cardPoints={cardPoints} age={age} servicePlans={servicePlans} />
      )}
      {loading && <Loading />}
    </div>
  );
};

export default CardSelectorTool;
